import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { Menu, PartyPopper, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonStyles } from '@/lib/button-styles'
import { navLinks } from '@/constants/site'
import { invitation } from '@/constants/invitation'

const SiteHeader: FC = (): ReactNode => {
    const [scrolled, setScrolled] = useState(false)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 24)

        handleScroll()
        window.addEventListener('scroll', handleScroll, { passive: true })

        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    useEffect(() => {
        if (!open) return

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        window.addEventListener('keydown', handleKeyDown)

        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [open])

    const elevated = scrolled || open

    return (
        <header className='fixed inset-x-0 top-0 z-50 px-3 pt-3 sm:px-5 sm:pt-4'>
            <div
                className={cn(
                    'mx-auto flex max-w-6xl items-center justify-between gap-3 rounded-full py-2 pr-2 pl-3 transition-all duration-300 sm:pl-4',
                    elevated ? 'bg-card/85 shadow-lg shadow-foreground/5 ring-1 ring-foreground/10 backdrop-blur-md' : 'bg-transparent'
                )}
            >
                <a
                    href='/#accueil'
                    className='flex items-center gap-2.5 rounded-full outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40'
                >
                    <span className='grid size-9 place-items-center rounded-full bg-primary text-primary-foreground'>
                        <PartyPopper className='size-4' aria-hidden='true' />
                    </span>

                    <span className='font-heading text-xl leading-none'>
                        {invitation.honoree}
                    </span>

                    <span className='rounded-full bg-accent px-2 py-0.5 text-xs font-bold text-accent-foreground'>
                        {invitation.age} ans
                    </span>
                </a>

                <nav aria-label='Navigation principale' className='hidden md:block'>
                    <ul className='flex items-center gap-1'>
                        {navLinks.map(link => (
                            <li key={link.href}>
                                <a
                                    href={link.href}
                                    className='rounded-full px-3.5 py-2 text-sm font-medium text-foreground/75 transition-colors outline-none hover:bg-foreground/5 hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/40'
                                >
                                    {link.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='flex items-center gap-2'>
                    <a href='/#rsvp' className={buttonStyles({ size: 'sm' })}>
                        Répondre
                    </a>

                    <button
                        type='button'
                        aria-expanded={open}
                        aria-controls='menu-mobile'
                        aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
                        onClick={() => setOpen(value => !value)}
                        className='grid size-9 place-items-center rounded-full bg-card/80 ring-1 ring-foreground/10 transition-colors outline-none hover:bg-card focus-visible:ring-[3px] focus-visible:ring-ring/40 md:hidden'
                    >
                        {open ? <X className='size-4' aria-hidden='true' /> : <Menu className='size-4' aria-hidden='true' />}
                    </button>
                </div>
            </div>

            <nav
                id='menu-mobile'
                aria-label='Navigation mobile'
                hidden={!open}
                className='mx-auto mt-2 max-w-6xl rounded-3xl bg-card/95 p-2 shadow-xl ring-1 ring-foreground/10 backdrop-blur-md animate-in fade-in slide-in-from-top-2 duration-200 motion-reduce:animate-none md:hidden'
            >
                <ul>
                    {navLinks.map(link => (
                        <li key={link.href}>
                            <a
                                href={link.href}
                                onClick={() => setOpen(false)}
                                className='block rounded-2xl px-4 py-3 text-base font-medium transition-colors outline-none hover:bg-foreground/5 focus-visible:ring-[3px] focus-visible:ring-ring/40'
                            >
                                {link.label}
                            </a>
                        </li>
                    ))}
                </ul>
            </nav>
        </header>
    )
}

export default SiteHeader
